import React from "react"
import { StaticQuery, graphql } from "gatsby"
import { globalDataType, pageBlokType } from "../../types"
import { CourseTemplateWrapperBlokProps } from "../s22_course-template"
import { CourseTemplateCmp } from "../s22_course-template/CourseTemplate"
import Select from "../input-v2/Select"
import Input from "../input-v2/Input"
import IconV2 from "../icon-v2"
import {
    StyledOverview,
    StyledOverviewToolbar,
    StyledOverviewMobileTools,
    StyledOverviewFilterToggle,
    StyledOverviewFilters,
    StyledOverviewFiltersHeader,
    StyledOverviewFiltersHeaderBack,
    StyledOverviewFiltersContent,
    StyledOverviewInput,
    StyledOverviewResults,
    StyledOverviewItem,
    StyledOverviewAction,
    StyledOverviewAlert,
} from "./partials"
import ButtonV2 from "../button-v2"

export interface OverviewProps {
    blok: pageBlokType
    degree: Array<string>
    program: Array<string>
    type: Array<string>
    module: Array<string>
    init_items_value: number
    load_more_value: number
    hide_filters: boolean
    hide_programs: boolean

    hide_degree_filter: boolean
    hide_program_filter: boolean
    hide_type_filter: boolean
}

declare global {
    interface Array<T> {
        intersects(list: Array<T>): Array<T>;
        isFilter(list: Array<T>): boolean;
    }
}


Array.prototype.intersects = function (list: any[]): any[] {
    return this?.filter((i: any) => list.includes(i))
}

Array.prototype.isFilter = function (list: any[]): boolean {
    return this.length === 0 || this.intersects(list).length > 0
}

const S22Overview: React.FC<OverviewProps> = ({
    degree,
    program,
    type,
    module,
    init_items_value,
    load_more_value,
    hide_filters,
    hide_programs,
    hide_degree_filter,
    hide_program_filter,
    hide_type_filter,
}) => {
    function parseQueryResult(
        data: any,
        degreeFilter: Array<string>,
        typeFilter: Array<string>,
        moduleFilter: Array<string>,
        programFilter: Array<string>,
        titleFilter: string
    ): Array<CourseTemplateWrapperBlokProps> {
        const parsedResult: Array<CourseTemplateWrapperBlokProps> = []
        data.allStoryblokEntry.edges.forEach((edge: any) => {
            const { node } = edge
            let content: any = node.content
            if (typeof node.content === "string") content = JSON.parse(node.content)

            if (
                (degreeFilter.isFilter(content.degree)) &&
                (typeFilter.isFilter(content.type)) &&
                (moduleFilter.isFilter(content.module)) &&
                (programFilter.isFilter(content.programs)) &&
                (!titleFilter || content.title.toLowerCase().indexOf(titleFilter) > -1)
            ) {
                parsedResult.push({ blok: content } as CourseTemplateWrapperBlokProps)
            }
        })
        return parsedResult
    }

    function sendGaEvent(
        value: string,
        label: string
    ) {
        if (window && typeof window !== "undefined") {
            window.dataLayer.push({
                event: "courseOverviewFilter",
                courseOverviewFilterValue: value,
                courseOverviewFilterLabel: label,
            })
        }
    }

    const [selectedDegree, setSelectedDegree] = React.useState<string[]>(degree)
    const [selectedProgram, setSelectedProgram] = React.useState<string[]>(program)
    const [selectedType, setSelectedType] = React.useState<string[]>(type)
    const [selectedModule, setSelectedModule] = React.useState<string[]>(module)
    const [titleFilter, setTitleFilter] = React.useState<string>("")
    const [showTotal, setShowTotal] = React.useState<number>(init_items_value)
    const [showFilters, setShowFilters] = React.useState<boolean>(false)

    return (
        <StaticQuery
            query={graphql`
        query {
          allStoryblokDatasourceEntry(
            filter: {
              data_source: {
                in: ["course-degree", "course-program", "course-type"]
              }
            }
          ) {
            nodes {
              data_source
              name
              value
              dimension_value
              data_source_dimension
            }
          }
          allStoryblokEntry(
            filter: { field_component: { eq: "s22_course-template" } }
            sort: { fields: [field_type_string, slug], order: [ASC, ASC] }
          ) {
            edges {
              node {
                id
                slug
                full_slug
                content
                published_at
                position
                field_type_string
              }
            }
          }
        }
      `}
            render={(data) => {
                let stories = parseQueryResult(
                    data,
                    selectedDegree,
                    selectedType,
                    selectedModule,
                    selectedProgram,
                    titleFilter
                )
                return (
                    <StyledOverview hide_filters={hide_filters}>
                        <StyledOverviewToolbar hide_filters={hide_filters}>
                            <StyledOverviewMobileTools>
                                <StyledOverviewFilterToggle
                                    onClick={() => setShowFilters(!showFilters)}
                                >
                                    <p>Filter Courses</p>
                                    <IconV2 size="16" icon="filter" />
                                </StyledOverviewFilterToggle>
                                <Input
                                    label="Search for a Course"
                                    isDark={true}
                                    placeholder="Start typing to search..."
                                    value={titleFilter}
                                    onChange={(event) => {
                                        if (event.target.value.length === 3) {
                                            sendGaEvent(event.target.value, "Course Text Search")
                                        }
                                        setTitleFilter(event.target.value.toLowerCase())
                                    }}
                                />
                            </StyledOverviewMobileTools>
                            <StyledOverviewFilters visible={showFilters}>
                                <StyledOverviewFiltersHeader>
                                    <StyledOverviewFiltersHeaderBack
                                        onClick={() => setShowFilters(!showFilters)}
                                    >
                                        <IconV2 size="16" icon="arrow-left" />
                                    </StyledOverviewFiltersHeaderBack>
                                    <p>Filter Courses</p>
                                </StyledOverviewFiltersHeader>
                                <StyledOverviewFiltersContent>
                                    <StyledOverviewInput desktop_only={true}>
                                        <Input
                                            label="Search for a Course"
                                            isDark={true}
                                            value={titleFilter}
                                            placeholder="Start typing to search..."
                                            onChange={(event) => {
                                                if (event.target.value.length === 3) {
                                                    sendGaEvent(event.target.value, "Course Text Search")
                                                }
                                                setTitleFilter(event.target.value.toLowerCase())
                                            }}
                                        />
                                    </StyledOverviewInput>
                                    {!hide_degree_filter && (
                                        <StyledOverviewInput>
                                            <Select
                                                onChange={(event) => {
                                                    sendGaEvent(event.target.value, "Degree Filter")
                                                    if (event.target.value === "all")
                                                        setSelectedDegree(degree)
                                                    else setSelectedDegree([event.target.value])
                                                }}
                                                defaultValue="all"
                                                label="Degree"
                                                isDark={true}
                                            >
                                                <option value="all">All Degrees</option>
                                                {data.allStoryblokDatasourceEntry.nodes
                                                    .filter(
                                                        (entry) => entry.data_source === "course-degree"
                                                    )
                                                    .map((option) => (
                                                        (degree.length === 0 || degree.includes(option.value)) &&
                                                        <option key={option.value} value={option.value}>
                                                            {option.name}
                                                        </option>
                                                    ))}
                                            </Select>
                                        </StyledOverviewInput>
                                    )}
                                    {!hide_program_filter && (
                                        <StyledOverviewInput>
                                            <Select
                                                onChange={(event) => {
                                                    sendGaEvent(event.target.value, "Program Filter")
                                                    if (event.target.value === "all")
                                                        setSelectedProgram(program)
                                                    else setSelectedProgram([event.target.value])
                                                }}
                                                label="Program"
                                                isDark={true}
                                                defaultValue="all"
                                            >
                                                <option value="all">All Programs</option>
                                                {data.allStoryblokDatasourceEntry.nodes
                                                    .filter(
                                                        (entry) =>
                                                            entry.data_source === "course-program" &&
                                                            entry.data_source_dimension === "path"
                                                    )
                                                    .map((option) => (
                                                        (program.length === 0 || program.includes(option.value)) &&
                                                        <option key={option.value} value={option.value}>
                                                            {option.name}
                                                        </option>
                                                    ))}
                                            </Select>
                                        </StyledOverviewInput>
                                    )}
                                    {!hide_type_filter && (
                                        <StyledOverviewInput>
                                            <Select
                                                onChange={(event) => {
                                                    sendGaEvent(event.target.value, "Type Filter")
                                                    if (event.target.value === "all") setSelectedType(type)
                                                    else setSelectedType([event.target.value])
                                                }}
                                                label="Type"
                                                isDark={true}
                                                defaultValue="all"
                                                value="all"
                                            >
                                                <option value="all">All Course Types</option>
                                                {data.allStoryblokDatasourceEntry.nodes
                                                    .filter(
                                                        (entry) => entry.data_source === "course-type"
                                                    )
                                                    .map((option) => (
                                                        (type.length === 0 || type.includes(option.value)) &&
                                                        <option key={option.value} value={option.value}>
                                                            {option.name}
                                                        </option>
                                                    ))}
                                            </Select>
                                        </StyledOverviewInput>
                                    )}
                                    <StyledOverviewAction mobile_only={true}>
                                        <ButtonV2
                                            className="button-v2--primary"
                                            label="Show Results"
                                            onClick={() => setShowFilters(!showFilters)}
                                        />
                                    </StyledOverviewAction>
                                </StyledOverviewFiltersContent>
                            </StyledOverviewFilters>
                        </StyledOverviewToolbar>
                        {stories && stories.length > 0 ? (
                            <>
                                <StyledOverviewResults>
                                    {stories.slice(0, showTotal).map((s) => (
                                        <StyledOverviewItem>
                                            <CourseTemplateCmp
                                                title={s.blok.title}
                                                description={s.blok.description}
                                                type={s.blok.type}
                                                degree={s.blok.degree}
                                                module={s.blok.module}
                                                programs={s.blok.programs}
                                                topics={s.blok.topics}
                                                hide_programs={hide_programs}
                                            />
                                        </StyledOverviewItem>
                                    ))}
                                </StyledOverviewResults>
                                {showTotal < stories.length && (
                                    <StyledOverviewAction>
                                        <ButtonV2
                                            className="button-v2--secondary"
                                            label="Show More"
                                            icon="ArrowDown"
                                            onClick={() => setShowTotal(showTotal + load_more_value)}
                                        />
                                    </StyledOverviewAction>
                                )}
                            </>
                        ) : (
                            <StyledOverviewAlert>No results found.</StyledOverviewAlert>
                        )}
                    </StyledOverview>
                )
            }}
        />
    )
}

export default S22Overview
